import { OrionBreadcrumbs, type OrionBreadcrumbItem } from "./OrionBreadcrumbs";
import { MuiBox } from "../MuiComponents/MuiBox";
import { MuiTypography } from "../MuiComponents/MuiTypography";
import type { SxProps, Theme } from "@mui/material/styles";
import type { ReactNode } from "react";

export type OrionPageHeaderProps = {
  /**
   * Structured breadcrumb trail. All items except the last render as clickable
   * links; the last renders as static text for the current page.
   */
  breadcrumbs?: OrionBreadcrumbItem[];
  title: ReactNode;
  /** Rendered inline after the title (e.g. status chip). */
  titleAdornment?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  /**
   * Optional inline controls rendered between the title and the actions
   * (e.g. a name field + filters on create pages), keeping the header to a
   * single row. Wraps onto its own full-width row below the `lg` breakpoint.
   *
   * The column is centred on the header itself (not between title and
   * actions), so it lines up with a page body of the same max width.
   */
  centerContent?: ReactNode;
  /**
   * Max width (px) of the `centerContent` column. Match the page body's
   * content width so header controls align with what they filter. Default 720.
   */
  centerContentMaxWidth?: number;
  /** Vertical padding in theme spacing units. Default 4. */
  paddingY?: number;
  /** Horizontal padding in theme spacing units. Default 6. */
  paddingX?: number;
  sx?: SxProps<Theme>;
};

export function OrionPageHeader({
  breadcrumbs,
  title,
  titleAdornment,
  description,
  actions,
  centerContent,
  centerContentMaxWidth = 720,
  paddingY = 4,
  paddingX = 6,
  sx,
}: OrionPageHeaderProps) {
  const hasCenterContent = centerContent != null;

  return (
    <MuiBox
      sx={{
        ...(hasCenterContent
          ? {
              // Grid with equal 1fr side tracks keeps the centre column
              // centred on the page; sides never shrink below their content.
              display: "grid",
              alignItems: "center",
              columnGap: 6,
              rowGap: 3,
              gridTemplateColumns: {
                xs: "minmax(0, 1fr) auto",
                lg: `minmax(max-content, 1fr) minmax(0, ${centerContentMaxWidth}px) minmax(max-content, 1fr)`,
              },
              gridTemplateAreas: {
                xs: `"title actions" "center center"`,
                lg: `"title center actions"`,
              },
            }
          : {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }),
        py: paddingY,
        px: paddingX,
        ...sx,
      }}
    >
      <MuiBox
        sx={
          hasCenterContent
            ? { gridArea: "title", minWidth: 0 }
            : { flex: 1, minWidth: 0 }
        }
      >
        {breadcrumbs != null && breadcrumbs.length > 0 ? (
          <OrionBreadcrumbs items={breadcrumbs} />
        ) : null}
        {titleAdornment != null ? (
          <MuiBox
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              columnGap: 2,
              rowGap: 2,
              mb: description != null ? "0.35em" : 0,
            }}
          >
            <MuiTypography variant="h5" component="div" sx={{ minWidth: 0 }}>
              {title}
            </MuiTypography>
            <MuiBox sx={{ flexShrink: 0 }}>{titleAdornment}</MuiBox>
          </MuiBox>
        ) : (
          <MuiTypography variant="h5" gutterBottom={Boolean(description)}>
            {title}
          </MuiTypography>
        )}
        {description != null ? (
          <MuiTypography variant="body2" sx={{ color: "text.primary" }}>
            {description}
          </MuiTypography>
        ) : null}
      </MuiBox>
      {hasCenterContent ? (
        <MuiBox
          sx={{
            gridArea: "center",
            alignSelf: "end",
            justifySelf: "center",
            display: "flex",
            minWidth: 0,
            width: "100%",
            maxWidth: centerContentMaxWidth,
          }}
        >
          {centerContent}
        </MuiBox>
      ) : null}
      {actions != null ? (
        <MuiBox
          sx={{
            alignSelf: "flex-end",
            display: "flex",
            gap: 2,
            ...(hasCenterContent && {
              gridArea: "actions",
              justifySelf: "end",
            }),
          }}
        >
          {actions}
        </MuiBox>
      ) : null}
    </MuiBox>
  );
}
